import { HttpResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AgencyService } from '@pulpo/pulpo-api';
import {
  AGENCY_DOCUMENT_ACCEPT,
  AGENCY_DOCUMENT_LABELS,
  AGENCY_DOCUMENT_MAX_SIZE,
  AgencyDocument,
  AgencyDocumentSlug,
  IAgency,
} from '@pulpo/pulpo-models';
import { Subscription } from 'rxjs';
import { DeleteDialogComponent } from '../delete-dialog/delete-dialog.component';
import { PulpoSnackbarComponent } from '../pulpo-snackbar/pulpo-snackbar.component';
import { clearPossibleSubscriptions } from '../../utils/subscription-helper';
import {
  openPreviewTab,
  saveBlob,
  showBlobInTab,
} from '../../utils/blob-helper';

/**
 * Documents of an agency, each one viewable, downloadable,
 * replaceable and deletable from the back-office. Every change is
 * saved at once by the user service, independently of the agency form.
 */
@Component({
  selector: 'agency-documents',
  templateUrl: './agency-documents.component.html',
  styleUrls: ['./agency-documents.component.scss'],
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
})
export class AgencyDocumentsComponent implements OnDestroy {
  @Input() agencyId?: string;
  @Input() documents: Partial<Record<AgencyDocumentSlug, AgencyDocument | null>> = {};
  /** The agency as the backend returns it after an upload or a deletion. */
  @Output() changed = new EventEmitter<IAgency>();
  /**
   * True while an upload or a deletion is in flight: the backend then saves
   * the whole agency, which would overwrite a form saved meanwhile.
   */
  @Output() busyChange = new EventEmitter<boolean>();

  readonly slugs = Object.keys(AGENCY_DOCUMENT_LABELS) as AgencyDocumentSlug[];
  readonly labels = AGENCY_DOCUMENT_LABELS;
  readonly accept = AGENCY_DOCUMENT_ACCEPT;
  /** The slug whose upload or deletion is in flight. */
  busySlug: AgencyDocumentSlug | null = null;

  private downloadSubscription: Subscription;
  private writeSubscription: Subscription;
  private dialogSubscription: Subscription;

  private readonly agencyService = inject(AgencyService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnDestroy(): void {
    clearPossibleSubscriptions(
      this.downloadSubscription,
      this.writeSubscription,
      this.dialogSubscription
    );
  }

  download(slug: AgencyDocumentSlug): void {
    this.fetch(slug, (blob, filename) => saveBlob(blob, filename));
  }

  /** Opens the document in a new tab, still through the authenticated call. */
  preview(slug: AgencyDocumentSlug): void {
    const tab = openPreviewTab();
    this.fetch(
      slug,
      (blob, filename) => showBlobInTab(tab, blob, filename),
      () => tab?.close()
    );
  }

  /** Adds the picked file, or replaces the stored one. */
  upload(slug: AgencyDocumentSlug, input: HTMLInputElement): void {
    const file = input.files?.[0];
    // Cleared so that picking the same file again still fires `change`
    input.value = '';
    if (!file || !this.agencyId || this.busySlug) {
      return;
    }
    // Some systems give no type at all: the backend then decides
    if (file.type && !this.accept[slug].includes(file.type)) {
      this.notify('Format non accepté pour ce document.');
      return;
    }
    if (file.size > AGENCY_DOCUMENT_MAX_SIZE) {
      this.notify('Fichier trop lourd (10 Mo maximum).');
      return;
    }
    const replacing = !!this.documents[slug];
    this.write(
      slug,
      this.agencyService.uploadDocument(this.agencyId, slug, file),
      replacing ? 'Document remplacé.' : 'Document ajouté.'
    );
  }

  remove(slug: AgencyDocumentSlug): void {
    const agencyId = this.agencyId;
    if (!agencyId || this.busySlug) {
      return;
    }
    clearPossibleSubscriptions(this.dialogSubscription);
    this.dialogSubscription = this.dialog
      .open(DeleteDialogComponent, {
        data: {
          id: slug,
          content: `Supprimer « ${this.labels[slug]} » ? Le fichier sera définitivement effacé.`,
          confirmLabel: 'Oui, supprimer le document',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === slug) {
          this.write(
            slug,
            this.agencyService.deleteDocument(agencyId, slug),
            'Document supprimé.'
          );
        }
      });
  }

  private write(
    slug: AgencyDocumentSlug,
    request: ReturnType<AgencyService['deleteDocument']>,
    successMessage: string
  ): void {
    this.setBusy(slug);
    clearPossibleSubscriptions(this.writeSubscription);
    this.writeSubscription = request.subscribe({
      next: (res: HttpResponse<IAgency>) => {
        this.setBusy(null);
        if (res.body) {
          this.changed.emit(res.body);
        }
        this.notify(successMessage);
        this.cdr.markForCheck();
      },
      // HTTP errors are already surfaced by ErrorHandlerInterceptor
      error: (err) => {
        this.setBusy(null);
        console.error(err);
        this.cdr.markForCheck();
      },
    });
  }

  private setBusy(slug: AgencyDocumentSlug | null): void {
    this.busySlug = slug;
    this.busyChange.emit(slug !== null);
  }

  private fetch(
    slug: AgencyDocumentSlug,
    onLoaded: (blob: Blob, filename: string) => void,
    onError?: () => void
  ): void {
    if (!this.agencyId) {
      onError?.();
      return;
    }
    clearPossibleSubscriptions(this.downloadSubscription);
    this.downloadSubscription = this.agencyService
      .downloadDocument(this.agencyId, slug)
      .subscribe({
        next: (res: HttpResponse<Blob>) => {
          if (!res.body) {
            onError?.();
            return;
          }
          onLoaded(res.body, filenameOf(res, this.documents[slug], slug));
        },
        error: (err) => {
          console.error(err);
          onError?.();
        },
      });
  }

  private notify(message: string): void {
    this.snackBar.openFromComponent(PulpoSnackbarComponent, {
      data: { title: 'Documents', message },
      duration: 4000,
      panelClass: ['snackbar'],
    });
  }
}

/**
 * The name the backend gives the file, else the uploaded name with the
 * extension of what is served (a logo comes back as a PNG whatever its upload
 * format), else the slug.
 */
export function filenameOf(
  res: HttpResponse<Blob>,
  document: AgencyDocument | null | undefined,
  slug: AgencyDocumentSlug
): string {
  const disposition = res.headers?.get('Content-Disposition') ?? '';
  // Only `filename*=` is percent-encoded: a plain `filename=` may hold a `%`
  const encoded = /filename\*=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      return encoded[1];
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  if (plain) {
    return plain[1];
  }
  return withServedExtension(
    document?.originalFilename || slug,
    res.body?.type || document?.contentType
  );
}

const EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
};

function withServedExtension(name: string, contentType?: string | null): string {
  const extension = contentType ? EXTENSIONS[contentType] : undefined;
  if (!extension) {
    return name;
  }
  const dot = name.lastIndexOf('.');
  return (dot > 0 ? name.slice(0, dot) : name) + '.' + extension;
}
